<template>
  <div
    class="application-component creativity-analysis-component"
    v-loading="initializing"
    :element-loading-text="loadingText"
  >
    <div class="application-component-title">创造性分析</div>
    <div
      class="application-component-content"
      :style="{ fontSize }"
    >
      <div v-if="readOnly" class="placeholder">
        <el-skeleton :rows="8" animated />
      </div>
      <template v-else>
        <div v-if="!hasNoveltyResult" class="dependency-tip">
          <div class="tip-title">创造性分析依赖新颖性分析结果</div>
          <div class="tip-desc">请先完成「新颖性分析」模块。</div>
        </div>
        <template v-else>
          <div v-if="noveltyStale" class="stale-tip">
            <span>新颖性分析结果已更新，当前创造性分析可能已过期。</span>
            <el-button size="small" text @click="handleInitialize">重新计算</el-button>
          </div>
          <div v-else-if="cardsStale" class="stale-tip">
            <span>最接近现有技术或差异特征已变化，请重新执行“特征差异分析”。</span>
          </div>

          <div v-if="rows.length" class="analysis-table-scroll">
            <table class="analysis-table creativity-table">
              <thead>
                <tr>
                  <th class="code-column">编号</th>
                  <th class="feature-column">本申请技术特征</th>
                  <th
                    v-for="document in documents"
                    :key="document.refCode"
                    class="doc-column"
                  >
                    <div class="doc-head">
                      <div class="doc-head-top">
                        <button class="doc-head-code-button" type="button" @click="handlePreviewDocument(document)">
                          <span class="doc-head-code">{{ document.refCode }}</span>
                        </button>
                        <button
                          class="doc-head-action"
                          type="button"
                          @click.stop="handleToggleClosest(document.refCode)"
                        >
                          {{ document.selectedClosest ? '取消' : '选择' }}
                        </button>
                      </div>
                      <button class="doc-head-preview" type="button" @click="handlePreviewDocument(document)">
                        <span v-if="document.selectedClosest" class="doc-head-closest">
                          <span class="doc-head-star">⭐</span>
                          <span>最接近现有技术</span>
                        </span>
                        <span class="doc-head-title">{{ document.pn }}</span>
                        <span class="doc-head-score">
                          相似度 {{ formatScore(document.semanticsScore) }}
                          <span class="doc-head-score-gap"></span>
                          公开程度 {{ formatScore(document.disclosureScore) }}
                        </span>
                      </button>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in rows" :key="row.code">
                  <td class="code-column cell-code">{{ row.code }}</td>
                  <td class="feature-column">
                    <button class="feature-cell-button" type="button" @click="handleFocusFeature(row)">
                      <div class="feature-cell-header">
                        <span class="feature-code-tag">{{ row.code }}</span>
                        <span v-if="row.difference" class="difference-badge">差异特征</span>
                      </div>
                      <div class="feature-cell-content textselection">{{ row.content }}</div>
                    </button>
                  </td>
                  <td
                    v-for="cell in row.compareItems"
                    :key="`${row.code}-${cell.refCode}`"
                    class="doc-column"
                  >
                    <div :class="['compare-cell', { selected: cell.selectedClosest, difference: cell.difference }]">
                      <div class="compare-cell-header">
                        <span :class="['status-tag', getStatusClass(cell.status)]">
                          {{ cell.statusLabel }}
                        </span>
                        <span v-if="cell.difference" class="difference-tag">差异</span>
                      </div>
                      <button
                        v-if="isCompareCellFocusable(cell)"
                        class="compare-text compare-text-button textselection"
                        type="button"
                        @click="handleFocusCompareCell(cell)"
                      >
                        {{ cell.content }}
                      </button>
                      <div v-else class="compare-text textselection">{{ cell.content }}</div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <el-empty v-else description="完成新颖性分析后，将在此生成创造性分析表。" />

          <div v-if="rows.length" :class="['difference-footer', { warning: !differenceCount }]">
            <div class="difference-summary">
              <template v-if="differenceCount">
                差异特征已识别：{{ differenceCount }} 条
              </template>
              <template v-else>
                该技术方案可能不具备新颖性和创造性
              </template>
            </div>
            <div class="difference-actions">
              <el-button
                type="primary"
                size="small"
                :disabled="!differenceCount"
                :loading="generatingCards || initializing"
                @click="handleGenerateCards({
                  force: true,
                  successText: cards.length ? '差异分析卡片已更新' : '差异分析卡片已生成',
                  scrollIntoView: !cards.length
                })"
              >
                特征差异分析
              </el-button>
            </div>
            <!-- <div v-if="!cards.length" class="difference-actions">
              <el-button
                type="primary"
                size="small"
                :disabled="!differenceCount"
                :loading="generatingCards"
                @click="handleGenerateCards"
              >
                特征差异分析
              </el-button>
            </div> -->
          </div>

          <div
            ref="cardsSectionRef"
            class="cards-section"
            v-loading="generatingCards"
            :element-loading-text="loadingText"
          >
            <div class="cards-header">
              <div>
                <div class="cards-title">差异分析卡片</div>
                <div class="cards-desc">每张卡片支持编辑、删除和加入交流清单。</div>
              </div>
              <el-button size="small" @click="handleAddCard">新增卡片</el-button>
            </div>

            <div v-if="cards.length" class="card-list">
              <div v-for="(card, index) in cards" :key="card.id || index" class="analysis-card">
                <div class="card-head">
                  <div class="card-head-title">
                    <span class="card-index">卡片 {{ index + 1 }}</span>
                    <span class="card-index card-index--feature">特征 {{ card.featureCode || index + 1 }}</span>
                    <span :class="['judgement-pill', getJudgementClass(card.creativityJudgement)]">
                      {{ card.creativityJudgementLabel }}
                    </span>
                  </div>
                  <div class="card-head-actions">
                    <el-button
                      v-if="handleUpdateDetailList || handleAddToDetailList"
                      text
                      size="small"
                      @click="handleAddCardToDetail(card)"
                    >
                      加入交流清单
                    </el-button>
                    <el-button text size="small" @click="handleDeleteCard(index)">删除</el-button>
                  </div>
                </div>

                <div class="card-field">
                  <div class="field-label">差异特征（编号）</div>
                  <el-input v-model="card.featureCode" class="field-inline-input" placeholder="例如 F1" @blur="handleCardBlur" />
                  <el-input v-model="card.featureContent" type="textarea" :autosize="{ minRows: 2, maxRows: 4 }" placeholder="请输入差异特征内容" @blur="handleCardBlur" />
                </div>
                <div class="card-field">
                  <div class="field-label">技术效果</div>
                  <el-input v-model="card.technicalEffect" type="textarea" :autosize="{ minRows: 2, maxRows: 4 }" placeholder="请输入技术效果" @blur="handleCardBlur" />
                </div>
                <div class="card-field">
                  <div class="field-label">显而易见分析</div>
                  <el-input v-model="card.obviousnessAnalysis" type="textarea" :autosize="{ minRows: 3, maxRows: 6 }" placeholder="请输入显而易见分析" @blur="handleCardBlur" />
                </div>
                <div class="card-field">
                  <div class="field-label">单项创造性判断</div>
                  <el-select v-model="card.creativityJudgement" class="field-select" @change="handleJudgementChange(card)">
                    <el-option v-for="item in judgementOptions" :key="item.value" :label="item.label" :value="item.value" />
                  </el-select>
                </div>
                <div class="card-field">
                  <div class="field-label">改进建议</div>
                  <el-input v-model="card.improvementSuggestion" type="textarea" :autosize="{ minRows: 2, maxRows: 5 }" placeholder="请输入改进建议" @blur="handleCardBlur" />
                </div>
              </div>
            </div>
            <el-empty v-else :description="differenceCount ? '差异分析卡片将自动生成。' : '当前没有可分析的差异特征。'" />
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ElMessage } from 'element-plus'
import { computed, nextTick, ref, watch } from 'vue'
import { useGlobalStore } from '@/store/modules/global'
import { useWritingStore } from '@/store/modules/writing'
import {
  fetchGenerateCreativityCards,
  fetchInitializeCreativityAnalysis,
  fetchSaveComponent,
  fetchSearchNoveltyDocument
} from '@/api/writing'
import { jsonParse } from '@/utils'

const CREATIVITY_COMPONENT_CODE = 'comp_cre'
const NOVELTY_COMPONENT_CODE = 'comp_nov'
const DETAIL_LIST_COMPONENT_CODE = 'comp_comm'
const FEATURE_BREAKDOWN_EVENT = 'feature-breakdown-focus'

const judgementOptions = [
  { value: 'INVENTIVE', label: '具备创造性' },
  { value: 'QUESTIONABLE', label: '创造性存疑' },
  { value: 'NOT_INVENTIVE', label: '不具备创造性' }
]

const globalStore = useGlobalStore()
const writingStore = useWritingStore()

const props = defineProps({
  readOnly: {
    type: Boolean,
    default: false
  },
  data: {
    type: Object,
    default: () => null
  },
  projectId: {
    type: String,
    default: ''
  },
  handleFocusMainFileSource: {
    type: Function,
    default: null
  },
  handleFocusCompareFileSource: {
    type: Function,
    default: null
  },
  handleAddToDetailList: {
    type: Function,
    default: null
  },
  handleUpdateDetailList: {
    type: Function,
    default: null
  }
})

const resultData = ref(null)
const initializing = ref(false)
const generatingCards = ref(false)
const hasAutoInitialized = ref(false)
const cardsSectionRef = ref(null)

const fontSize = computed(() => `${globalStore.fontSize}px`)
const loadingText = computed(() => {
  return (initializing.value || generatingCards.value) ? '正在思考中...' : ''
})

const noveltyRaw = computed(() => writingStore.step2Info?.step2Component?.[NOVELTY_COMPONENT_CODE]?.docResult || '')
const hasNoveltyResult = computed(() => Boolean(normalizeNoveltyResult(noveltyRaw.value)))
const documents = computed(() => resultData.value?.documents || [])
const rows = computed(() => resultData.value?.rows || [])
const cards = computed(() => resultData.value?.cards || [])
const differenceCount = computed(() => resultData.value?.differenceCount || 0)
const cardsStale = computed(() => Boolean(resultData.value?.cardsStale))
const noveltyStale = computed(() => {
  if (!resultData.value?.noveltyVersion || !noveltyRaw.value) {
    return false
  }
  return resultData.value.noveltyVersion !== hashJavaString(noveltyRaw.value)
})

const documentMap = computed(() => {
  return documents.value.reduce((result, item) => {
    result[item.refCode] = item
    return result
  }, {})
})

const normalizeNoveltyResult = (value) => {
  const parsed = typeof value === 'string' ? jsonParse(value, null) : value
  if (!parsed || typeof parsed !== 'object') {
    return null
  }
  if (!Array.isArray(parsed.documents) || !Array.isArray(parsed.rows)) {
    return null
  }
  return parsed
}

const getJudgementLabel = (value) => {
  return judgementOptions.find(item => item.value === value)?.label || '创造性存疑'
}

const normalizeCard = (item = {}, index = 0) => {
  const judgement = ['INVENTIVE', 'QUESTIONABLE', 'NOT_INVENTIVE'].includes(item?.creativityJudgement)
    ? item.creativityJudgement
    : 'QUESTIONABLE'

  return {
    id: item?.id || `creativity-card-${Date.now()}-${index}`,
    featureCode: item?.featureCode || '',
    featureContent: item?.featureContent || '',
    closestRefCodes: Array.isArray(item?.closestRefCodes) ? item.closestRefCodes.filter(Boolean) : [],
    technicalEffect: item?.technicalEffect || '',
    obviousnessAnalysis: item?.obviousnessAnalysis || '',
    creativityJudgement: judgement,
    creativityJudgementLabel: item?.creativityJudgementLabel || getJudgementLabel(judgement),
    improvementSuggestion: item?.improvementSuggestion || (judgement === 'INVENTIVE' ? '无需修改' : '')
  }
}

const normalizeCell = (item = {}) => {
  return {
    refCode: item?.refCode || '',
    pn: item?.pn || '',
    semanticsScore: item?.semanticsScore ?? null,
    status: item?.status || 'UNDISCLOSED',
    statusLabel: item?.statusLabel || '未公开',
    disclosureValue: Number(item?.disclosureValue ?? 0),
    selectedClosest: Boolean(item?.selectedClosest),
    difference: Boolean(item?.difference),
    content: item?.content || '',
    sourceRanges: Array.isArray(item?.sourceRanges) ? item.sourceRanges : []
  }
}

const normalizeRow = (item = {}) => {
  return {
    featureId: item?.featureId || '',
    code: item?.code || '',
    content: item?.content || '',
    sourceRanges: Array.isArray(item?.sourceRanges) ? item.sourceRanges : [],
    paragraphIndices: Array.isArray(item?.paragraphIndices) ? item.paragraphIndices : [],
    difference: Boolean(item?.difference),
    compareItems: Array.isArray(item?.compareItems) ? item.compareItems.map(normalizeCell) : []
  }
}

const normalizeDocument = (item = {}) => {
  return {
    refCode: item?.refCode || '',
    pn: item?.pn || '',
    semanticsScore: item?.semanticsScore ?? null,
    disclosureScore: item?.disclosureScore ?? 0,
    selectedClosest: Boolean(item?.selectedClosest),
    title: item?.title || '',
    fileName: item?.fileName || '',
    fileUrl: item?.fileUrl || '',
    filePath: item?.filePath || '',
    previewPath: item?.previewPath || '',
    viewerData: item?.viewerData || null
  }
}

const normalizeResult = (value) => {
  const parsed = typeof value === 'string' ? jsonParse(value, null) : value
  if (!parsed || typeof parsed !== 'object') {
    return null
  }

  const documentsValue = Array.isArray(parsed.documents) ? parsed.documents.map(normalizeDocument) : []
  const rowsValue = Array.isArray(parsed.rows) ? parsed.rows.map(normalizeRow) : []
  const cardsValue = Array.isArray(parsed.cards) ? parsed.cards.map(normalizeCard) : []

  return {
    noveltyVersion: parsed.noveltyVersion || '',
    updatedAt: parsed.updatedAt || '',
    featureCount: Number(parsed.featureCount || rowsValue.length || 0),
    differenceCount: Number(parsed.differenceCount ?? rowsValue.filter(item => item.difference).length),
    hasDifferenceFeatures: Boolean(parsed.hasDifferenceFeatures ?? rowsValue.some(item => item.difference)),
    cardsStale: Boolean(parsed.cardsStale),
    selectedRefCodes: Array.isArray(parsed.selectedRefCodes) ? parsed.selectedRefCodes.filter(Boolean) : [],
    documents: documentsValue,
    rows: rowsValue,
    cards: cardsValue
  }
}

const hashJavaString = (value = '') => {
  let hash = 0
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash * 31) + value.charCodeAt(index)) | 0
  }
  return (hash >>> 0).toString(16)
}

const syncStepState = (nextResult) => {
  const normalized = normalizeResult(nextResult)
  if (!normalized) {
    return
  }

  resultData.value = normalized
  writingStore.setStep2Info({
    ...(writingStore.step2Info || {}),
    step2Component: {
      ...(writingStore.step2Info?.step2Component || {}),
      [CREATIVITY_COMPONENT_CODE]: {
        ...(writingStore.step2Info?.step2Component?.[CREATIVITY_COMPONENT_CODE] || {}),
        ...(props.data || {}),
        docResult: JSON.stringify(normalized)
      }
    }
  })
}

const persistResult = async (nextResult) => {
  const normalized = normalizeResult(nextResult)
  if (!normalized || !props.projectId) {
    return
  }

  await fetchSaveComponent({
    disclosureProjectId: props.projectId,
    componentTypeCode: CREATIVITY_COMPONENT_CODE,
    docResult: JSON.stringify(normalized)
  })
  syncStepState(normalized)
}

const applyBackendResult = (result, successText = '') => {
  const normalized = normalizeResult(result)
  if (!normalized) {
    throw new Error('invalid_creativity_result')
  }

  syncStepState(normalized)
  if (successText) {
    ElMessage.success(successText)
  }
}

const formatScore = (score) => {
  if (score === null || score === undefined || Number.isNaN(Number(score))) {
    return '-'
  }
  return Number(score).toFixed(3)
}

const getStatusClass = (status) => {
  if (status === 'DISCLOSED') {
    return 'is-disclosed'
  }
  if (status === 'PARTIAL') {
    return 'is-partial'
  }
  return 'is-undisclosed'
}

const getJudgementClass = (judgement) => {
  if (judgement === 'INVENTIVE') {
    return 'is-inventive'
  }
  if (judgement === 'NOT_INVENTIVE') {
    return 'is-not-inventive'
  }
  return 'is-questionable'
}

const isCompareCellFocusable = (cell) => {
  return Boolean(
    cell &&
    cell.status !== 'UNDISCLOSED' &&
    Array.isArray(cell.sourceRanges) &&
    cell.sourceRanges.length
  )
}

const recalculateResult = (sourceResult, nextSelectedRefCodes, markCardsStale = false) => {
  const normalized = normalizeResult(sourceResult)
  if (!normalized) {
    return null
  }

  const orderedSelectedRefCodes = normalized.documents
    .map(item => item.refCode)
    .filter(refCode => nextSelectedRefCodes.includes(refCode))

  const nextDocuments = normalized.documents.map(document => ({
    ...document,
    selectedClosest: orderedSelectedRefCodes.includes(document.refCode)
  }))

  const nextRows = normalized.rows.map(row => {
    const compareItems = row.compareItems.map(item => ({
      ...item,
      selectedClosest: orderedSelectedRefCodes.includes(item.refCode),
      difference: false
    }))

    const selectedItems = compareItems.filter(item => item.selectedClosest)
    const difference = Boolean(selectedItems.length && !selectedItems.some(item => item.status === 'DISCLOSED'))
    selectedItems.forEach(item => {
      item.difference = difference
    })

    return {
      ...row,
      difference,
      compareItems
    }
  })

  return normalizeResult({
    ...normalized,
    selectedRefCodes: orderedSelectedRefCodes,
    differenceCount: nextRows.filter(item => item.difference).length,
    hasDifferenceFeatures: nextRows.some(item => item.difference),
    cardsStale: markCardsStale ? Boolean(normalized.cards.length) : normalized.cardsStale,
    documents: nextDocuments,
    rows: nextRows
  })
}

const handleInitialize = async ({ silent = false } = {}) => {
  if (!props.projectId || initializing.value || !hasNoveltyResult.value) {
    return false
  }

  initializing.value = true
  try {
    const result = await fetchInitializeCreativityAnalysis(props.projectId)
    applyBackendResult(result, silent ? '' : '创造性分析已更新')
    return true
  } catch (error) {
    console.error('initialize creativity analysis failed', error)
    ElMessage.error('创造性分析初始化失败')
    return false
  } finally {
    initializing.value = false
  }
}

const handleGenerateCards = async ({ force = false, successText = '', scrollIntoView = false } = {}) => {
  if (!props.projectId || generatingCards.value || initializing.value) {
    return false
  }
  if (noveltyStale.value) {
    const initialized = await handleInitialize({ silent: true })
    if (!initialized) {
      return false
    }
  }
  if (cards.value.length && !force) {
    return false
  }
  if (!differenceCount.value) {
    if (!force) {
      ElMessage.warning('当前没有可分析的差异特征')
    }
    return false
  }

  generatingCards.value = true
  try {
    const result = await fetchGenerateCreativityCards(props.projectId, {
      selectedRefCodes: resultData.value?.selectedRefCodes || []
    })
    applyBackendResult(result, successText)
    if (scrollIntoView) {
      await nextTick()
      cardsSectionRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    return true
  } catch (error) {
    console.error('generate creativity cards failed', error)
    ElMessage.error('差异分析卡片生成失败')
    return false
  } finally {
    generatingCards.value = false
  }
}

const handleToggleClosest = async (refCode) => {
  if (!resultData.value) {
    return
  }

  const currentSelectedRefCodes = resultData.value.selectedRefCodes || []
  const selectedSet = new Set(currentSelectedRefCodes)
  if (selectedSet.has(refCode) && selectedSet.size === 1) {
    ElMessage.warning('至少保留一个最接近现有技术')
    return
  }

  if (selectedSet.has(refCode)) {
    selectedSet.delete(refCode)
  } else {
    selectedSet.add(refCode)
  }

  const nextResult = recalculateResult(resultData.value, Array.from(selectedSet), true)
  if (!nextResult) {
    return
  }

  try {
    await persistResult(nextResult)
  } catch (error) {
    console.error('update creativity selection failed', error)
    ElMessage.error('最接近现有技术更新失败')
  }
}

const openDocumentTarget = (target) => {
  if (!target) {
    return false
  }
  window.open(target, '_blank')
  return true
}

const handlePreviewDocument = async (document) => {
  const fallbackTarget = document?.previewPath || document?.fileUrl

  if (props.projectId && document?.pn) {
    try {
      const refreshedDocument = await fetchSearchNoveltyDocument(props.projectId, {
        refCode: document.refCode,
        pn: document.pn,
        semanticsScore: document.semanticsScore
      })
      if (openDocumentTarget(refreshedDocument?.previewPath || refreshedDocument?.fileUrl)) {
        return
      }
    } catch (error) {
      console.error('refresh creativity document preview failed', error)
      if (fallbackTarget) {
        ElMessage.warning('实时获取失败，已尝试打开已有原文链接')
      }
    }
  }

  if (!openDocumentTarget(fallbackTarget)) {
    ElMessage.warning('暂无可预览文件')
  }
}

const handleFocusFeature = (row) => {
  if (props.handleFocusMainFileSource) {
    props.handleFocusMainFileSource({
      itemId: row?.featureId || row?.code || '',
      sourceRanges: row?.sourceRanges || []
    })
    return
  }
  window.dispatchEvent(new CustomEvent(FEATURE_BREAKDOWN_EVENT, {
    detail: {
      projectId: props.projectId,
      sourceRanges: row?.sourceRanges || []
    }
  }))
}

const handleFocusCompareCell = (cell) => {
  if (!isCompareCellFocusable(cell)) {
    return
  }

  const document = documentMap.value[cell.refCode]
  if (!document || !props.handleFocusCompareFileSource) {
    return
  }

  props.handleFocusCompareFileSource({
    document,
    cell
  })
}

const handleCardBlur = async () => {
  if (!resultData.value) {
    return
  }

  try {
    await persistResult(resultData.value)
  } catch (error) {
    console.error('save creativity cards failed', error)
    ElMessage.error('创造性分析保存失败')
  }
}

const handleJudgementChange = async (card) => {
  card.creativityJudgementLabel = getJudgementLabel(card.creativityJudgement)
  if (card.creativityJudgement === 'INVENTIVE') {
    card.improvementSuggestion = '无需修改'
  } else if (card.improvementSuggestion === '无需修改') {
    card.improvementSuggestion = ''
  }
  await handleCardBlur()
}

const handleAddCard = async () => {
  const firstDifferenceRow = rows.value.find(item => item.difference)
  const nextCard = normalizeCard({
    id: `creativity-card-${Date.now()}`,
    featureCode: firstDifferenceRow?.code || '',
    featureContent: firstDifferenceRow?.content || '',
    closestRefCodes: resultData.value?.selectedRefCodes || [],
    creativityJudgement: 'QUESTIONABLE',
    creativityJudgementLabel: getJudgementLabel('QUESTIONABLE'),
    improvementSuggestion: ''
  }, cards.value.length)

  const nextResult = normalizeResult({
    ...(resultData.value || {}),
    cards: [...cards.value, nextCard]
  })

  try {
    await persistResult(nextResult)
    await nextTick()
    cardsSectionRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  } catch (error) {
    console.error('add creativity card failed', error)
    ElMessage.error('新增卡片失败')
  }
}

const handleDeleteCard = async (index) => {
  const nextCards = cards.value.filter((_, cardIndex) => cardIndex !== index)
  const nextResult = normalizeResult({
    ...(resultData.value || {}),
    cards: nextCards
  })

  try {
    await persistResult(nextResult)
  } catch (error) {
    console.error('delete creativity card failed', error)
    ElMessage.error('删除卡片失败')
  }
}

const buildCardDetailItem = (card) => {
  const sourceId = `creativity:${card.id}`
  const row = rows.value.find(item => item.code === card.featureCode)
  const selectedCodes = Array.isArray(card.closestRefCodes) && card.closestRefCodes.length
    ? card.closestRefCodes
    : (resultData.value?.selectedRefCodes || [])
  const closestDocuments = selectedCodes
    .map(refCode => documentMap.value[refCode])
    .filter(Boolean)
  const compareContent = (row?.compareItems || [])
    .filter(item => selectedCodes.includes(item.refCode))
    .map(item => `${item.refCode}：“${item.content}”`)
    .join('\n')

  return {
    type: 'text',
    sourceId,
    question: `[创造性风险] ${card.featureCode || '未命名差异特征'} ${card.creativityJudgementLabel}`,
    answer: [
      '【创造性风险】',
      '',
      `涉及技术特征：${card.featureCode || ''} ${card.featureContent || ''}`.trim(),
      `最接近现有技术：${closestDocuments.map(item => `${item.refCode} ${item.pn}`).join('、') || '未选择'}`,
      `对比文件所涉内容：${compareContent || '暂无'}`,
      `问题说明：${card.obviousnessAnalysis || '暂无'}`,
      `修改建议：${card.improvementSuggestion || '暂无'}`
    ].join('\n')
  }
}

const handleAddCardToDetail = (card) => {
  const currentList = jsonParse(
    writingStore.step2Info?.step2Component?.[DETAIL_LIST_COMPONENT_CODE]?.docResult || '[]',
    []
  )
  const nextItem = buildCardDetailItem(card)

  if (currentList.some(item => item?.sourceId === nextItem.sourceId)) {
    ElMessage.warning('该问题已加入交流清单')
    return
  }

  if (props.handleUpdateDetailList) {
    props.handleUpdateDetailList([
      ...currentList,
      nextItem
    ])
  } else if (props.handleAddToDetailList) {
    props.handleAddToDetailList(nextItem)
  }

  ElMessage.success('已加入交流清单')
}

watch(
  () => props.data?.docResult,
  (newValue) => {
    resultData.value = normalizeResult(newValue)
  },
  { immediate: true }
)

watch(
  () => noveltyRaw.value,
  async (newValue) => {
    if (!newValue || props.readOnly || resultData.value || hasAutoInitialized.value) {
      return
    }

    hasAutoInitialized.value = true
    await handleInitialize()
  },
  { immediate: true }
)

</script>

<style scoped lang="scss">
@import './common.scss';

.creativity-analysis-component {
  position: relative;
  min-height: 0;
}

.dependency-tip,
.stale-tip,
.difference-footer,
.cards-section {
  border-radius: 16px;
}

.dependency-tip,
.stale-tip,
.difference-footer {
  padding: 14px 16px;
}

.dependency-tip {
  background: #fff7ed;
  border: 1px solid #fed7aa;
}

.tip-title,
.cards-title {
  color: #111827;
  font-weight: 600;
}

.tip-title {
  color: #9a3412;
}

.tip-desc,
.cards-desc {
  margin-top: 6px;
  line-height: 1.7;
  color: #4b5563;
}

.stale-tip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: #9a3412;
  background: #fff7ed;
  border: 1px solid #fdba74;
}

.analysis-table-scroll {
  margin-top: 16px;
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
}

.analysis-table {
  min-width: 920px;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;

  th,
  td {
    padding: 14px 12px;
    font-size: inherit;
    text-align: left;
    vertical-align: top;
    border-bottom: 1px solid #e5e7eb;
    border-right: 1px solid #e5e7eb;
  }

  th:last-child,
  td:last-child {
    border-right: none;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f8fafc;
    color: #111827;
    font-weight: 600;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}

.code-column {
  width: 78px;
}

.feature-column {
  width: 240px;
}

.doc-column {
  width: 220px;
}

.cell-code {
  color: #111827;
  font-weight: 700;
}

.doc-head {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.doc-head-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.doc-head-preview,
.doc-head-code-button,
.doc-head-action,
.feature-cell-button,
.compare-text-button {
  font: inherit;
  color: inherit;
  border: none;
  background: transparent;
  cursor: pointer;
}

.doc-head-code-button {
  display: inline-flex;
  align-items: center;
  padding: 0;
}

.doc-head-preview {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  padding: 0;
  text-align: left;
}

.doc-head-action {
  padding: 0;
  color: #0f766e;
  font-size: inherit;
  line-height: 1.5;
  white-space: nowrap;
}

.doc-head-action:hover,
.doc-head-action:focus {
  color: #0d9488;
}

.doc-head-code,
.feature-code-tag,
.card-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  color: #0f766e;
  background: #ecfdf5;
  border-radius: 999px;
  font-weight: 600;
}

.card-head-title > .card-index:first-child {
  display: none;
}

.doc-head-closest {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: inherit;
  color: #0f766e;
}

.doc-head-star {
  font-size: inherit;
}

.doc-head-title {
  color: #0f172a;
  font-weight: 600;
  word-break: break-all;
}

.doc-head-score {
  color: #4b5563;
  font-size: inherit;
  line-height: 1.6;
}

.doc-head-score-gap {
  display: inline-block;
  width: 16px;
}

.feature-cell-button {
  width: 100%;
  padding: 0;
  text-align: left;
}

.feature-cell-header,
.compare-cell-header,
.cards-header,
.card-head,
.card-head-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.feature-cell-content,
.compare-text {
  font-size: inherit;
  margin-top: 10px;
  line-height: 1.75;
  color: #334155;
  white-space: pre-wrap;
  word-break: break-word;
}

.compare-text-button {
  display: block;
  width: 100%;
  padding: 0;
  text-align: left;
}

.compare-cell {
  padding: 12px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;

  &.selected {
    background: #f8fafc;
    border-color: #cbd5e1;
  }

  &.difference {
    border-color: #0f766e;
    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.08);
  }
}

.status-tag,
.difference-tag,
.difference-badge,
.judgement-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  font-size: inherit;
  border-radius: 999px;
}

.status-tag {
  width: fit-content;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
}

.status-tag.is-disclosed {
  color: #b91c1c;
  background: #fee2e2;
}

.status-tag.is-partial {
  color: #b45309;
  background: #ffedd5;
}

.status-tag.is-undisclosed {
  color: #4b5563;
  background: #f3f4f6;
}

.difference-tag,
.difference-badge {
  color: #0f766e;
  background: #ccfbf1;
}

.difference-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  background: #f8fafc;
  border: 1px solid #dbe4ee;

  &.warning {
    background: #fff7ed;
    border-color: #fdba74;
  }
}

.difference-summary {
  color: #111827;
  font-weight: 600;
}

.difference-actions {
  display: flex;
  gap: 10px;
}

.cards-section {
  margin-top: 18px;
  padding: 18px;
  background: #fff;
  border: 1px solid #e5e7eb;
}

.cards-desc {
  margin-top: 4px;
}

.card-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 16px;
}

.analysis-card {
  padding: 16px;
  background: linear-gradient(180deg, #ffffff 0%, #fafafa 100%);
  border: 1px solid #e5e7eb;
  border-radius: 16px;
}

.card-head {
  margin-bottom: 14px;
}

.card-head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.judgement-pill.is-inventive {
  color: #166534;
  background: #dcfce7;
}

.judgement-pill.is-questionable {
  color: #9a3412;
  background: #ffedd5;
}

.judgement-pill.is-not-inventive {
  color: #b91c1c;
  background: #fee2e2;
}

.card-field + .card-field {
  margin-top: 14px;
}

.field-label {
  margin-bottom: 8px;
  color: #111827;
  font-weight: 600;
}

.field-inline-input,
.field-select {
  margin-bottom: 8px;
}

@media (max-width: 768px) {
  .difference-footer,
  .cards-header,
  .card-head {
    flex-direction: column;
    align-items: stretch;
  }

  .difference-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
